<template>
  <b-modal
    :id="modalId"
    centered
    title="Update Activity"
    @hide="$emit('close')"
    hide-header-close
    @show="resetModal"
    @hidden="onHidden"
    @ok.prevent="handleOk"
    size="lg"
    :hide-footer="
      !data?.is_allow_update ||
      data?.status === 'completed' ||
      data?.status === 'in review' ||
      (data?.status === 'in progress' && data?.count_progress_activity !== 100)
    "
  >
    <div class="p-2 d-flex flex-column gap-4">
      <!-- Course Info -->
      <b-row class="d-flex flex-column rounded bg-dark-soft p-4 gap-3">
        <b-col class="d-flex flex-column gap-1 col-12">
          <label>Task</label>
          <h4>{{ data?.task }}</h4>
        </b-col>

        <b-row class="d-flex flex-row justify-content-between align-items-center">
          <b-col class="d-flex flex-column gap-1 col-4">
            <label>Start Date</label>
            <h4>{{ formatDate(data?.start_date) }}</h4>
          </b-col>

          <b-col class="d-flex flex-column gap-1 col-4">
            <label>End Date</label>
            <h4>{{ formatDate(data?.end_date) }}</h4>
          </b-col>

          <b-col class="d-flex flex-column gap-1 col-4">
            <label>Percentage</label>
            <h4>{{ data?.percentage }}%</h4>
          </b-col>
        </b-row>

        <b-row class="d-flex flex-row align-items-center">
          <b-col v-if="data?.actual_start_date" class="d-flex flex-column gap-1 col-4">
            <label>Actual Start Date</label>
            <h4>{{ formatDate(data?.actual_start_date) }}</h4>
          </b-col>

          <b-col v-if="data?.actual_end_date" class="d-flex flex-column gap-1 col-4">
            <label>Actual End Date</label>
            <h4>{{ formatDate(data?.actual_end_date) }}</h4>
          </b-col>

          <b-col class="d-flex flex-column gap-0 col-4">
            <label>Status</label>
            <span :class="getTrainingStatusClass(data?.status)" class="mr-auto">
              {{ snakeToTitle(data?.status) }}
            </span>
          </b-col>
        </b-row>

        <b-col class="d-flex flex-column gap-1 col-4">
          <label>Assignee</label>
          <ul
            v-if="data?.employees?.length"
            class="list-unstyled mb-0"
            v-for="(employee, index) in data?.employees"
            :key="index"
          >
            <h4>{{ employee?.name }}</h4>
          </ul>
        </b-col>
      </b-row>

      <b-form ref="form" @submit.stop.prevent="handleSubmit">
        <b-row v-if="!data?.actual_start_date && data?.is_allow_update">
          <b-col class="col-4">
            <b-form-group
              label="Actual Start Date"
              :state="getState('actual_start_date')"
              invalid-feedback="Actual Start Tanggal wajib diisi."
            >
              <v-date-picker
                v-model="form.actual_start_date"
                type="date"
                format="DD-MM-YYYY"
                placeholder="Select Date"
                :disabled-date="disableDateBefore"
                @input="clearError('actual_start_date')"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-col
          v-if="data?.status === 'in progress' && data?.count_progress_activity !== 100 && data?.is_allow_update"
          class="d-flex flex-row align-items-center justify-content-end p-0"
        >
          <b-button class="ml-auto mr-0" variant="primary" @click="openAddProgress">
            <b-spinner v-if="isLoading" small type="grow" class="me-2"></b-spinner>
            <span>{{ isLoading ? "Adding..." : "Add Progress" }}</span>
          </b-button>
        </b-col>

        <b-form-group v-if="!(data?.status === 'to do' && data?.is_allow_update)" label="Progress Task">
          <vue-good-table
            :columns="progressTaskColumns"
            :rows="data?.progress"
            style-class="vgt-table border-0 table-auto"
            styleClass="vgt-table condensed"
          >
            <div slot="emptystate" class="text-center font-size-14">No data available</div>

            <template slot="table-row" slot-scope="props">
              <span v-if="props.column.field === 'date'">
                <div class="d-flex flex-row align-items-center justify-content-start">
                  <span>{{ formatDate(props.row.date) }}</span>
                </div>
              </span>

              <span v-else-if="props.column.field === 'attachments'">
                <div class="d-flex flex-row align-items-center justify-content-start">
                  <a v-if="props.row.attachments" :href="props.row.attachments" target="_blank">File</a>
                  <span v-else>No evidence available</span>
                </div>
              </span>

              <span v-else-if="props.column.field === 'progress'">
                <div class="d-flex flex-row align-items-center justify-content-start">
                  <span>{{ props.row.progress || 0 }}%</span>
                </div>
              </span>

              <span v-else class="d-flex align-items-center justify-content-start">
                {{ props.formattedRow[props.column.field] }}
              </span>
            </template>
          </vue-good-table></b-form-group
        >

        <b-form-group v-if="!(data?.status === 'to do' && data?.is_allow_update)" label="Progress" disabled>
          <div class="d-flex align-items-center gap-2">
            <b-form-input
              :value="data?.count_progress_activity"
              type="range"
              min="0"
              max="100"
              step="1"
              class="flex-grow-1"
              disabled
            />
            <div class="progress-value ms-2">{{ data?.count_progress_activity || 0 }}%</div>
          </div>
        </b-form-group>

        <b-form-group v-if="data?.evidence" label="Evidence">
          <div class="d-flex flex-column gap-1 w-75">
            <a v-if="data?.evidence" class="font-weight-bold" :href="data?.evidence" target="_blank">Evidence File</a>
          </div>
        </b-form-group>

        <b-form-group
          v-if="data?.count_progress_activity === 100 && !data?.evidence && data?.is_allow_update"
          label="Evidence"
          :state="getState('evidence')"
          invalid-feedback="Evidence is required."
        >
          <p class="text-muted small mb-2">
            Upload your evidence in image or PDF format (JPG, JPEG, PNG, PDF). Max file size: 2MB
          </p>

          <div
            class="dropzone p-4 border rounded text-center"
            :class="{ 'border-danger': isDragging, 'is-invalid': !selectedFile && submitted }"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleDrop"
          >
            <div v-if="!selectedFile && !previewUrl">
              <i class="fe fe-upload-cloud text-muted mb-2" style="font-size: 3rem"></i>
              <p class="text-muted mb-2">Drag & drop your evidence here or</p>
              <label class="btn btn-outline-danger btn-sm">
                Browse Files
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,application/pdf"
                  @change="handleFileSelect"
                  hidden
                />
              </label>
            </div>

            <div v-else>
              <img
                v-if="previewUrl && !isPdf"
                :src="previewUrl"
                alt="Certificate Preview"
                class="img-fluid rounded mb-3"
                style="max-height: 250px"
              />
              <div v-else-if="isPdf" class="pdf-preview mb-3">
                <div class="pdf-icon-container">
                  <i class="fe fe-file-text"></i>
                </div>
                <p class="pdf-label mb-0">PDF File</p>
              </div>
              <div class="d-flex align-items-center justify-content-center gap-2">
                <span class="text-success">
                  <i class="fe fe-check-circle me-1"></i>
                  {{ selectedFile?.name || "N/A" }}
                </span>
                <button class="btn btn-outline-danger btn-sm" @click="removeFile">
                  <i class="fe fe-x"></i>
                </button>
              </div>
              <p class="text-muted small mt-2 mb-0">Size: {{ selectedFile ? formatFileSize(selectedFile?.size) : "N/A" }}</p>
            </div>
          </div>
          <small v-if="!selectedFile && submitted" class="text-danger"> Please select a evidence file </small>
        </b-form-group>
      </b-form>
    </div>

    <template #modal-footer>
      <b-button variant="primary" @click="handleOk" :disabled="isLoading || !data.is_allow_update" :key="isLoading">
        <b-spinner v-if="isLoading" small type="grow" class="me-2"></b-spinner>
        <span>{{ isLoading ? "Updating..." : "Update" }}</span>
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import moment from "moment";
import { formatDate, formatTimeToHHMM, isPdfFile, getTrainingStatusClass, snakeToTitle, cleanObject } from "../utils/utils";

export default {
  name: "UpdateActivityModal",

  props: {
    modalId: { type: String, default: "update-activity-modal" },
    data: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
  },

  data() {
    return {
      form: this.getInitialForm(),
      selectedFile: null,
      previewUrl: null,
      isPdf: false,
      isDragging: false,
      submitted: false,
      formErrors: {},
      suppressValidation: false,
      progressTaskColumns: [
        {
          label: "Name",
          field: "name",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Date",
          field: "date",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Progress",
          field: "progress",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Attachment",
          field: "attachments",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
      ],
    };
  },

  methods: {
    formatTimeToHHMM,
    isPdfFile,
    formatDate,
    getTrainingStatusClass,
    snakeToTitle,

    disableDateBefore(date) {
      const today = moment().startOf("day");
      const current = moment(date);
      return current.isBefore(today, "day");
    },

    clearError(field) {
      if (this.formErrors[field]) {
        this.$delete(this.formErrors, field);
      }
    },

    getState(field) {
      if (this.formErrors[field]) return false;
      return null;
    },

    onHidden() {
      this.resetModal();
      this.$emit("close");
    },

    openAddProgress() {
      this.$emit("open-add-progress");
    },

    handleOk(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.handleSubmit();
    },

    validateAll() {
      this.formErrors = {};

      if (!this.form.actual_start_date && this.data.is_allow_update && this.data.status === "to do") {
        this.formErrors.actual_start_date = "Actual Start Tanggal wajib diisi.";
      }

      if (!this.form.evidence && this.data.is_allow_update && this.data.count_progress_activity === 100) {
        this.formErrors.evidence = "Evidence is required.";
      }

      return Object.keys(this.formErrors).length === 0;
    },

    handleSubmit() {
      const isValid = this.validateAll();
      if (!isValid) return;

      const payload = { ...this.form };

      if (this.form.actual_start_date) payload.actual_start_date = this.formatDateOnly(this.form.actual_start_date);

      this.$emit("update-activity", cleanObject(payload));
    },

    handleChangeFile(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.form.evidence = file;
      this.form.id = this.data.id;
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      this.processFile(file);
    },

    handleDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      this.processFile(file);
    },

    formatDateOnly(dateStr) {
      if (!dateStr) return null;
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    processFile(file) {
      if (!file) return;

      const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
      if (!allowedTypes.includes(file.type)) {
        this.$swal({
          title: "Tipe File Tidak Valid",
          text: "Unggah file gambar (JPG, JPEG, PNG) atau PDF",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      const maxSize = 2 * 1024 * 1024;
      if (file.size > maxSize) {
        this.$swal({
          title: "File Terlalu Besar",
          text: "Ukuran file maksimal 2MB",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      this.selectedFile = file;
      this.isPdf = file.type === "application/pdf";

      this.form.evidence = file;

      if (!this.isPdf) {
        this.previewUrl = URL.createObjectURL(file);
      } else {
        this.previewUrl = null;
      }

      this.formErrors.evidence = null;
    },

    removeFile() {
      this.selectedFile = null;
      this.isPdf = false;
      this.form.evidence = null;

      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
      }
    },

    formatFileSize(bytes) {
      if (!bytes) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },

    getInitialForm() {
      return {
        actual_start_date: null,
        evidence: null,
        id: this.data?.id,
      };
    },

    async resetModal() {
      if (this.isLoading) return;

      this.suppressValidation = true;

      await this.$nextTick();

      this.form = this.getInitialForm();
      this.formErrors = {};
      this.selectedFile = null;
      this.previewUrl = null;

      this.$nextTick(() => {
        this.suppressValidation = false;
      });
    },
  },

  beforeDestroy() {
    // Clean up object URL
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
  },
};
</script>

<style scoped>
.dropzone {
  background-color: #f8f9fa;
  border: 2px dashed #dee2e6 !important;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dropzone:hover,
.dropzone.border-danger {
  border-color: #e63757 !important;
  background-color: #fff5f7;
}

.dropzone.is-invalid {
  border-color: #dc3545 !important;
}

.course-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #fce4e4;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gap-2 {
  gap: 0.5rem;
}

.me-1 {
  margin-right: 0.25rem;
}

.me-2 {
  margin-right: 0.5rem;
}

.me-3 {
  margin-right: 1rem;
}

/* PDF Preview Styles */
.pdf-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.pdf-preview .pdf-icon-container {
  width: 80px;
  height: 100px;
  background: linear-gradient(135deg, #e63757 0%, #c82333 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(230, 55, 87, 0.3);
}

.pdf-preview .pdf-icon-container i {
  font-size: 2.5rem;
  color: #fff;
}

.pdf-preview .pdf-label {
  margin-top: 1rem;
  font-weight: 600;
  color: #1a202c;
}

:deep(.table-header) {
  font-size: 11px !important;
}

:deep(.vgt-table td, .vgt-table th) {
  font-size: 11px !important;
}
</style>
